import React, { useCallback, useEffect, useRef, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const fmt = (n, decimals = 0) =>
  Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const fmtMoney = (n) => '₹ ' + fmt(n, 2);

const fmtDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
};

const fmtDateTime = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 16);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
};

const PERIOD_LABELS = { today: 'Today', mtd: 'MTD', ytd: 'YTD' };


const pillStyle = (active) => ({
  padding: '0.2rem 0.65rem',
  border: active ? '2px solid var(--theme-primary-dark)' : '1.5px solid var(--theme-border)',
  borderRadius: '999px',
  background: active ? '#eef4ff' : 'var(--theme-bg-light)',
  color: active ? 'var(--theme-primary-dark)' : 'var(--theme-text-secondary)',
  cursor: 'pointer',
  fontSize: '0.73rem',
  fontWeight: active ? 700 : 500
});


function PmsDashboard({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('today');
  const [lastRefresh, setLastRefresh] = useState(null);
  const intervalRef = useRef(null);

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  const loadDashboard = useCallback(async () => {
    if (!token || !selectedProperty?.id || !selectedProperty?.chain_id) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/v1/pms/dashboard', { headers: authHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) { setError(json.error || 'Failed to load dashboard data'); return; }
      setData(json);
      setLastRefresh(new Date());
    } catch {
      setError('Network error loading dashboard');
    } finally {
      setLoading(false);
    }
  }, [token, selectedProperty, authHeaders]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(loadDashboard, 60000);
    return () => clearInterval(intervalRef.current);
  }, [loadDashboard]);

  if (!selectedProperty?.id) {
    return (
      <div className="page-shell" style={{ color: 'var(--theme-text-secondary)' }}>
        Select a property to view the dashboard.
      </div>
    );
  }

  const rev = data?.revenue || {};
  const occ = data?.occupancy || {};
  const folios = data?.open_folios || {};
  const outstanding = data?.outstanding || {};
  const trend = Array.isArray(data?.trend) ? data.trend : [];
  const recentTxns = Array.isArray(data?.recent_transactions) ? data.recent_transactions : [];
  const movement = data?.today_movement || {};

  // Trend chart helpers
  const maxTrend = trend.length ? Math.max(...trend.map((r) => r.total), 1) : 1;

  const isCreditWarn = (row) => {
    const limit = Number(row.credit_limit || 0);
    const used = Number(row.credit_used || 0);
    return limit > 0 && used >= limit * 0.9;
  };

  const creditWarnCorps = (outstanding.corporates || []).filter(isCreditWarn);
  const creditWarnTas = (outstanding.travel_agents || []).filter(isCreditWarn);
  const totalAlerts =
    (folios.count > 0 ? 1 : 0) +
    creditWarnCorps.length +
    creditWarnTas.length;

  return (
    <div style={{ padding: '1.5rem', fontFamily: 'system-ui, sans-serif', background: 'var(--theme-bg-light)', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e293b', fontSize: '1.35rem', fontWeight: 800 }}>PMS Dashboard</h2>
          <div style={{ color: 'var(--theme-text-secondary)', fontSize: '0.8rem', marginTop: '0.2rem' }}>
            {selectedProperty?.name || ''} &nbsp;·&nbsp; {lastRefresh ? `Updated ${lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Loading…'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          {/* Period selector */}
          {['today', 'mtd', 'ytd'].map((p) => (
            <button key={p} type="button" onClick={() => setPeriod(p)} style={pillStyle(period === p)}>
              {PERIOD_LABELS[p]}
            </button>
          ))}
          <button
            type="button"
            onClick={loadDashboard}
            disabled={loading}
            style={{ padding: '0.35rem 0.85rem', border: '1.5px solid var(--theme-primary-dark)', borderRadius: '8px', background: loading ? 'var(--theme-border)' : 'var(--theme-primary-dark)', color: loading ? 'var(--theme-text-muted)' : '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
          >
            {loading ? '…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">{error}</div>
      )}

      {/* ── Summary Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(190px, 1fr))', gap: isMobile ? '0.5rem' : '0.85rem', marginBottom: '1.25rem' }}>

        {/* Room Revenue */}
        <div className="card card-body" style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Room Revenue</div>
          <div style={{ fontSize: '1.45rem', fontWeight: 800, color: '#1e293b', marginTop: '0.3rem' }}>
            {fmtMoney(rev.room?.[period])}
          </div>
          <div style={{ fontSize: '0.73rem', color: 'var(--theme-text-muted)', marginTop: '0.3rem' }}>
            MTD {fmtMoney(rev.room?.mtd)} &nbsp;·&nbsp; YTD {fmtMoney(rev.room?.ytd)}
          </div>
        </div>

        {/* F&B Revenue */}
        <div className="card card-body" style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>F&B Revenue</div>
          <div style={{ fontSize: '1.45rem', fontWeight: 800, color: '#1e293b', marginTop: '0.3rem' }}>
            {fmtMoney(rev.fnb?.[period])}
          </div>
          <div style={{ fontSize: '0.73rem', color: 'var(--theme-text-muted)', marginTop: '0.3rem' }}>
            MTD {fmtMoney(rev.fnb?.mtd)} &nbsp;·&nbsp; YTD {fmtMoney(rev.fnb?.ytd)}
          </div>
        </div>

        {/* Total Revenue */}
        <div className="card card-body" style={{ minWidth: 0, border: '2px solid var(--theme-primary-dark)', background: '#eef4ff' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--theme-primary-dark)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Revenue</div>
          <div style={{ fontSize: '1.55rem', fontWeight: 800, color: '#1e40af', marginTop: '0.3rem' }}>
            {fmtMoney(rev.total?.[period])}
          </div>
          <div style={{ fontSize: '0.73rem', color: '#3b82f6', marginTop: '0.3rem' }}>
            MTD {fmtMoney(rev.total?.mtd)} &nbsp;·&nbsp; YTD {fmtMoney(rev.total?.ytd)}
          </div>
        </div>

        {/* Occupancy */}
        <div className="card card-body" style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Occupancy</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', marginTop: '0.3rem' }}>
            <span style={{ fontSize: '1.55rem', fontWeight: 800, color: occ.pct >= 80 ? '#15803d' : occ.pct >= 50 ? '#ca8a04' : '#b91c1c' }}>
              {occ.pct ?? 0}%
            </span>
          </div>
          <div style={{ fontSize: '0.73rem', color: 'var(--theme-text-muted)', marginTop: '0.3rem' }}>
            {fmt(occ.occupied ?? occ.inhouse)} occupied / {fmt(occ.sellable ?? occ.total_rooms)} sellable
          </div>
          {/* Mini occupancy bar */}
          <div style={{ background: 'var(--theme-border)', borderRadius: '99px', height: '5px', marginTop: '0.5rem', overflow: 'hidden' }}>
            <div style={{ background: occ.pct >= 80 ? 'var(--theme-success)' : occ.pct >= 50 ? 'var(--theme-warning)' : 'var(--theme-danger)', height: '100%', width: `${Math.min(occ.pct || 0, 100)}%`, borderRadius: '99px', transition: 'width 0.5s ease' }} />
          </div>
        </div>

        {/* Outstanding */}
        <div className="card card-body" style={{ minWidth: 0, borderLeft: outstanding.grand_total > 0 ? '4px solid #f59e0b' : '1px solid var(--theme-border)' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Outstanding</div>
          <div style={{ fontSize: '1.45rem', fontWeight: 800, color: outstanding.grand_total > 0 ? 'var(--theme-warning)' : '#1e293b', marginTop: '0.3rem' }}>
            {fmtMoney(outstanding.grand_total)}
          </div>
          <div style={{ fontSize: '0.73rem', color: 'var(--theme-text-muted)', marginTop: '0.3rem' }}>
            Corp {fmtMoney(outstanding.corporate_total)} &nbsp;·&nbsp; TA {fmtMoney(outstanding.ta_total)}
          </div>
        </div>
      </div>

      {/* ── Today's Movement ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(150px, 1fr))', gap: isMobile ? '0.5rem' : '0.7rem', marginBottom: '1.25rem' }}>
        {[
          { label: 'Expected Arrivals', value: fmt(movement.arrivals), color: '#2563eb', bg: '#dbeafe' },
          { label: 'Expected Departures', value: fmt(movement.departures), color: '#7c3aed', bg: '#ede9fe' },
          { label: 'In-House Guests', value: fmt(occ.inhouse), color: '#065f46', bg: '#d1fae5' },
          { label: 'Open Folios', value: fmt(folios.count), color: folios.count > 0 ? '#b45309' : '#064e3b', bg: folios.count > 0 ? '#fef3c7' : '#d1fae5' }
        ].map((item) => (
          <div key={item.label} style={{ background: item.bg, borderRadius: '10px', padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            <div style={{ fontSize: '0.71rem', color: item.color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{item.label}</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: item.color }}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* ── Charts + Alerts ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,2fr) minmax(0,1fr)', gap: isMobile ? '0.75rem' : '1rem', marginBottom: '1.25rem', alignItems: 'start' }}>

        {/* Revenue Trend Chart */}
        <div className="card card-body" style={{ minWidth: 0 }}>
          <div className="section-head">Revenue Trend — Last 14 Days</div>
          {trend.length === 0 ? (
            <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.85rem', padding: '1rem 0' }}>No trend data available</div>
          ) : (
            <>
              {/* Legend */}
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.7rem', fontSize: '0.73rem', fontWeight: 600 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <span style={{ width: '12px', height: '12px', background: '#3b82f6', borderRadius: '2px', display: 'inline-block' }} /> Room
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <span style={{ width: '12px', height: '12px', background: '#f59e0b', borderRadius: '2px', display: 'inline-block' }} /> F&B
                </span>
              </div>
              {/* Bars */}
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '120px', overflowX: 'auto', paddingBottom: '0.3rem' }}>
                {trend.map((row) => {
                  const totalH = maxTrend > 0 ? Math.max(2, Math.round((row.total / maxTrend) * 100)) : 2;
                  const roomPct = row.total > 0 ? (row.room_rev / row.total) * 100 : 100;
                  const fnbPct = 100 - roomPct;
                  const dayLabel = new Date(row.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }).replace(' ', '\n');
                  return (
                    <div key={row.date} style={{ flex: 1, minWidth: '28px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                      <div
                        title={`${fmtDate(row.date)}\nRoom: ${fmtMoney(row.room_rev)}\nF&B: ${fmtMoney(row.fnb_rev)}\nTotal: ${fmtMoney(row.total)}`}
                        style={{ width: '100%', height: `${totalH}px`, display: 'flex', flexDirection: 'column', borderRadius: '3px 3px 0 0', overflow: 'hidden', cursor: 'default' }}
                      >
                        <div style={{ height: `${fnbPct}%`, background: '#f59e0b', flexShrink: 0 }} />
                        <div style={{ height: `${roomPct}%`, background: '#3b82f6', flexShrink: 0 }} />
                      </div>
                      <div style={{ fontSize: '0.58rem', color: 'var(--theme-text-muted)', textAlign: 'center', lineHeight: 1.2, whiteSpace: 'pre' }}>
                        {dayLabel}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Y-axis label */}
              <div style={{ fontSize: '0.68rem', color: 'var(--theme-text-muted)', textAlign: 'right', marginTop: '0.3rem' }}>
                Max: {fmtMoney(maxTrend)}
              </div>
            </>
          )}
        </div>

        {/* Alerts Panel */}
        <div className="card card-body" style={{ minWidth: 0 }}>
          <div className="section-head" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Alerts & Exceptions
            {totalAlerts > 0 && (
              <span style={{ background: '#ef4444', color: '#fff', borderRadius: '999px', fontSize: '0.68rem', fontWeight: 700, padding: '0.1rem 0.45rem', minWidth: '18px', textAlign: 'center' }}>
                {totalAlerts}
              </span>
            )}
          </div>

          {totalAlerts === 0 ? (
            <div style={{ color: 'var(--theme-success)', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ fontSize: '1.1rem' }}>✓</span> No active exceptions
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>

              {/* Open folios */}
              {folios.count > 0 && (
                <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', padding: '0.55rem 0.8rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#92400e' }}>⚠ Open Folios ({fmt(folios.count)})</div>
                  <div style={{ fontSize: '0.73rem', color: '#b45309', marginTop: '0.2rem' }}>
                    Outstanding balance: {fmtMoney(folios.balance)}
                  </div>
                </div>
              )}

              {/* Credit limit warnings – corporates */}
              {creditWarnCorps.map((c) => (
                <div key={c.corporate_id} style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.55rem 0.8rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#991b1b' }}>⚠ Credit limit — {c.corporate_name}</div>
                  <div style={{ fontSize: '0.73rem', color: '#b91c1c', marginTop: '0.2rem' }}>
                    Used {fmtMoney(c.credit_used)} / {fmtMoney(c.credit_limit)} ({Math.round((Number(c.credit_used) / Number(c.credit_limit)) * 100)}%)
                  </div>
                </div>
              ))}

              {/* Credit limit warnings – travel agents */}
              {creditWarnTas.map((t) => (
                <div key={t.travel_agent_id} style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.55rem 0.8rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#991b1b' }}>⚠ TA Credit — {t.agent_name}</div>
                  <div style={{ fontSize: '0.73rem', color: '#b91c1c', marginTop: '0.2rem' }}>
                    Used {fmtMoney(t.credit_used)} / {fmtMoney(t.credit_limit)} ({Math.round((Number(t.credit_used) / Number(t.credit_limit)) * 100)}%)
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Outstanding Detail Table ── */}
      {((outstanding.corporates || []).length > 0 || (outstanding.travel_agents || []).length > 0) && (
        <div className="card card-body" style={{ marginBottom: '1.25rem' }}>
          <div className="section-head">Outstanding Details</div>
          <div style={{ overflowX: 'auto' }}>
            <table className="pms-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem', minWidth: '560px' }}>
              <thead>
                <tr style={{ background: 'var(--theme-border-light)' }}>
                  <th style={{ padding: '0.55rem 0.75rem', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid var(--theme-border)' }}>Type</th>
                  <th style={{ padding: '0.55rem 0.75rem', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid var(--theme-border)' }}>Name</th>
                  <th style={{ padding: '0.55rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#475569', borderBottom: '1px solid var(--theme-border)' }}>Credit Limit</th>
                  <th style={{ padding: '0.55rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#475569', borderBottom: '1px solid var(--theme-border)' }}>Used</th>
                  <th style={{ padding: '0.55rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#475569', borderBottom: '1px solid var(--theme-border)' }}>Available</th>
                  <th style={{ padding: '0.55rem 0.75rem', textAlign: 'center', fontWeight: 700, color: '#475569', borderBottom: '1px solid var(--theme-border)' }}>Utilisation</th>
                </tr>
              </thead>
              <tbody>
                {(outstanding.corporates || []).map((c, i) => {
                  const pct = Number(c.credit_limit) > 0 ? Math.round((Number(c.credit_used) / Number(c.credit_limit)) * 100) : 0;
                  const warn = isCreditWarn(c);
                  return (
                    <tr key={`corp-${c.corporate_id}`} style={{ background: warn ? '#fff7ed' : (i % 2 === 0 ? '#fff' : 'var(--theme-bg-light)'), borderBottom: '1px solid var(--theme-border-light)' }}>
                      <td style={{ padding: '0.55rem 0.75rem' }}><span style={{ background: '#dbeafe', color: '#1d4ed8', borderRadius: '4px', padding: '0.1rem 0.5rem', fontSize: '0.7rem', fontWeight: 700 }}>Corporate</span></td>
                      <td style={{ padding: '0.55rem 0.75rem', fontWeight: 600, color: '#1e293b' }}>{c.corporate_name}</td>
                      <td style={{ padding: '0.55rem 0.75rem', textAlign: 'right' }}>{fmtMoney(c.credit_limit)}</td>
                      <td style={{ padding: '0.55rem 0.75rem', textAlign: 'right', color: warn ? '#b91c1c' : '#1e293b', fontWeight: warn ? 700 : 400 }}>{fmtMoney(c.credit_used)}</td>
                      <td style={{ padding: '0.55rem 0.75rem', textAlign: 'right', color: Number(c.available_credit) < 0 ? '#b91c1c' : '#15803d' }}>{fmtMoney(c.available_credit)}</td>
                      <td style={{ padding: '0.55rem 0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <div style={{ flex: 1, background: 'var(--theme-border)', borderRadius: '99px', height: '6px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#22c55e', borderRadius: '99px' }} />
                          </div>
                          <span style={{ fontSize: '0.72rem', color: warn ? '#b91c1c' : 'var(--theme-text-secondary)', fontWeight: warn ? 700 : 400, minWidth: '32px', textAlign: 'right' }}>{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {(outstanding.travel_agents || []).map((t, i) => {
                  const pct = Number(t.credit_limit) > 0 ? Math.round((Number(t.credit_used) / Number(t.credit_limit)) * 100) : 0;
                  const warn = isCreditWarn(t);
                  return (
                    <tr key={`ta-${t.travel_agent_id}`} style={{ background: warn ? '#fff7ed' : (i % 2 === 0 ? '#fff' : 'var(--theme-bg-light)'), borderBottom: '1px solid var(--theme-border-light)' }}>
                      <td style={{ padding: '0.55rem 0.75rem' }}><span style={{ background: '#fce7f3', color: '#be185d', borderRadius: '4px', padding: '0.1rem 0.5rem', fontSize: '0.7rem', fontWeight: 700 }}>Travel Agent</span></td>
                      <td style={{ padding: '0.55rem 0.75rem', fontWeight: 600, color: '#1e293b' }}>{t.agent_name}</td>
                      <td style={{ padding: '0.55rem 0.75rem', textAlign: 'right' }}>{fmtMoney(t.credit_limit)}</td>
                      <td style={{ padding: '0.55rem 0.75rem', textAlign: 'right', color: warn ? '#b91c1c' : '#1e293b', fontWeight: warn ? 700 : 400 }}>{fmtMoney(t.credit_used)}</td>
                      <td style={{ padding: '0.55rem 0.75rem', textAlign: 'right', color: Number(t.available_credit) < 0 ? '#b91c1c' : '#15803d' }}>{fmtMoney(t.available_credit)}</td>
                      <td style={{ padding: '0.55rem 0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <div style={{ flex: 1, background: 'var(--theme-border)', borderRadius: '99px', height: '6px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#22c55e', borderRadius: '99px' }} />
                          </div>
                          <span style={{ fontSize: '0.72rem', color: warn ? '#b91c1c' : 'var(--theme-text-secondary)', fontWeight: warn ? 700 : 400, minWidth: '32px', textAlign: 'right' }}>{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Recent Transactions ── */}
      <div className="card card-body" style={{ marginBottom: '1.25rem' }}>
        <div className="section-head">Recent Folio Transactions</div>
        {recentTxns.length === 0 ? (
          <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.85rem' }}>No transactions found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="pms-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', minWidth: '620px' }}>
              <thead>
                <tr style={{ background: 'var(--theme-border-light)' }}>
                  {['Date', 'Type', 'Guest', 'Folio', 'Debit', 'Credit', 'Remarks'].map((h) => (
                    <th key={h} style={{ padding: '0.5rem 0.7rem', textAlign: h === 'Debit' || h === 'Credit' ? 'right' : 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid var(--theme-border)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentTxns.map((txn, i) => (
                  <tr key={txn.ledger_id} style={{ background: i % 2 === 0 ? '#fff' : 'var(--theme-bg-light)', borderBottom: '1px solid var(--theme-border-light)' }}>
                    <td style={{ padding: '0.48rem 0.7rem', whiteSpace: 'nowrap', color: '#475569' }}>{fmtDateTime(txn.posting_date || txn.created_at)}</td>
                    <td style={{ padding: '0.48rem 0.7rem' }}>
                      <span style={{
                        background: txn.posting_type === 'ROOM_CHARGE' ? '#dbeafe' : txn.posting_type?.includes('PAYMENT') ? '#dcfce7' : '#f3f4f6',
                        color: txn.posting_type === 'ROOM_CHARGE' ? '#1d4ed8' : txn.posting_type?.includes('PAYMENT') ? '#15803d' : 'var(--theme-text)',
                        borderRadius: '4px', padding: '0.1rem 0.45rem', fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap'
                      }}>
                        {String(txn.posting_type || '-').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={{ padding: '0.48rem 0.7rem', color: '#1e293b', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {txn.primary_guest_name || '-'}
                    </td>
                    <td style={{ padding: '0.48rem 0.7rem', color: 'var(--theme-text-secondary)', whiteSpace: 'nowrap' }}>#{txn.folio_id}</td>
                    <td style={{ padding: '0.48rem 0.7rem', textAlign: 'right', color: Number(txn.debit_amount) > 0 ? '#b91c1c' : 'var(--theme-text-muted)', fontWeight: Number(txn.debit_amount) > 0 ? 600 : 400 }}>
                      {Number(txn.debit_amount) > 0 ? fmtMoney(txn.debit_amount) : '-'}
                    </td>
                    <td style={{ padding: '0.48rem 0.7rem', textAlign: 'right', color: Number(txn.credit_amount) > 0 ? '#15803d' : 'var(--theme-text-muted)', fontWeight: Number(txn.credit_amount) > 0 ? 600 : 400 }}>
                      {Number(txn.credit_amount) > 0 ? fmtMoney(txn.credit_amount) : '-'}
                    </td>
                    <td style={{ padding: '0.48rem 0.7rem', color: 'var(--theme-text-secondary)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {txn.remarks || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer note */}
      <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-muted)', textAlign: 'center' }}>
        Dashboard refreshes automatically every 60 seconds &nbsp;·&nbsp; All amounts in INR &nbsp;·&nbsp; Revenue excludes payments &amp; refunds
      </div>
    </div>
  );
}

export default PmsDashboard;
